import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MethodologyShowcase = () => {
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    {
      name: "Discovery & Assessment",
      duration: "2-4 weeks",
      description: "Comprehensive analysis of current state, goals, and constraints",
      deliverables: [
        "AI Readiness Assessment Report",
        "Technology Stack Audit",
        "Risk & Compliance Analysis",
        "Strategic Roadmap Draft"
      ],
      activities: [
        "Stakeholder interviews and workshops",
        "Technical infrastructure review",
        "Data landscape mapping",
        "Competitive analysis and benchmarking"
      ],
      metrics: {
        "Stakeholders Interviewed": "15-25",
        "Systems Audited": "8-12",
        "Use Cases Identified": "20-30"
      }
    },
    {
      name: "Strategy & Planning",
      duration: "3-6 weeks",
      description: "Detailed implementation strategy with clear milestones and success criteria",
      deliverables: [
        "Implementation Blueprint",
        "Technology Selection Guide",
        "Resource Allocation Plan",
        "Success Metrics Framework"
      ],
      activities: [
        "Solution architecture design",
        "Team structure planning",
        "Timeline and milestone definition",
        "Risk mitigation strategy development"
      ],
      metrics: {
        "Solutions Designed": "3-5",
        "Milestones Defined": "12-18",
        "Risk Factors Addressed": "15-20"
      }
    },
    {
      name: "Implementation & Development",
      duration: "8-16 weeks",
      description: "Hands-on development and deployment with continuous stakeholder engagement",
      deliverables: [
        "Production-Ready AI Systems",
        "Integration Documentation",
        "Testing & Validation Reports",
        "User Training Materials"
      ],
      activities: [
        "Agile development sprints",
        "Continuous integration setup",
        "User acceptance testing",
        "Performance optimization"
      ],
      metrics: {
        "Features Delivered": "25-40",
        "Tests Automated": "200+",
        "Performance Improvement": "40-60%"
      }
    },
    {
      name: "Optimization & Scaling",
      duration: "4-8 weeks",
      description: "Performance tuning, scaling preparation, and knowledge transfer",
      deliverables: [
        "Performance Optimization Report",
        "Scaling Strategy Document",
        "Monitoring Dashboard",
        "Handover Documentation"
      ],
      activities: [
        "Performance monitoring setup",
        "Scaling architecture implementation",
        "Team training and knowledge transfer",
        "Continuous improvement planning"
      ],
      metrics: {
        "Performance Gains": "25-45%",
        "Team Members Trained": "8-15",
        "Monitoring Metrics": "50+"
      }
    }
  ];

  const totalDuration = "4-8 months";
  const successRate = "94%";

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-8 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-text-primary">
            Implementation Methodology
          </h3>
          <div className="flex items-center space-x-6 text-sm">
            <div className="text-center">
              <div className="font-bold text-brand-primary">{totalDuration}</div>
              <div className="text-text-secondary">Typical Duration</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-success">{successRate}</div>
              <div className="text-text-secondary">Success Rate</div>
            </div>
          </div>
        </div>
        <p className="text-text-secondary">
          Our proven 4-phase methodology ensures successful AI implementation with measurable outcomes at every stage.
        </p>
      </div>
      <div className="flex">
        {/* Phase Navigation */}
        <div className="w-1/3 border-r border-gray-200">
          {phases?.map((phase, index) => (
            <button
              key={index}
              onClick={() => setActivePhase(index)}
              className={`w-full p-6 text-left border-b border-gray-200 transition-all duration-200 ${
                activePhase === index
                  ? 'bg-brand-surface border-l-4 border-l-brand-primary' :'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  activePhase === index
                    ? 'bg-brand-primary text-white' :'bg-gray-200 text-text-secondary'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className={`font-semibold ${
                    activePhase === index ? 'text-brand-primary' : 'text-text-primary'
                  }`}>
                    {phase?.name}
                  </div>
                  <div className="text-sm text-text-secondary">{phase?.duration}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Phase Details */}
        <div className="flex-1 p-8">
          <div className="mb-6">
            <h4 className="text-xl font-bold text-text-primary mb-2">
              {phases?.[activePhase]?.name}
            </h4>
            <p className="text-text-secondary mb-4">
              {phases?.[activePhase]?.description}
            </p>
            <div className="inline-flex items-center space-x-2 bg-brand-surface px-3 py-1 rounded-full">
              <Icon name="Clock" size={16} color="var(--color-brand-primary)" />
              <span className="text-sm font-medium text-brand-primary">
                {phases?.[activePhase]?.duration}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div>
              <h5 className="font-semibold text-text-primary mb-3 flex items-center">
                <Icon name="Package" size={18} color="var(--color-brand-primary)" className="mr-2" />
                Key Deliverables
              </h5>
              <ul className="space-y-2">
                {phases?.[activePhase]?.deliverables?.map((deliverable, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{deliverable}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-text-primary mb-3 flex items-center">
                <Icon name="Activity" size={18} color="var(--color-brand-primary)" className="mr-2" />
                Core Activities
              </h5>
              <ul className="space-y-2">
                {phases?.[activePhase]?.activities?.map((activity, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="ArrowRight" size={16} color="var(--color-brand-primary)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{activity}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h5 className="font-semibold text-text-primary mb-3 flex items-center">
              <Icon name="BarChart3" size={18} color="var(--color-brand-primary)" className="mr-2" />
              Success Metrics
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Object.entries(phases?.[activePhase]?.metrics)?.map(([metric, value], index) => (
                <div key={index} className="bg-brand-surface rounded-lg p-4 text-center">
                  <div className="text-lg font-bold text-brand-primary">{value}</div>
                  <div className="text-sm text-text-secondary">{metric}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MethodologyShowcase;